import { useState } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, EmptyState } from "@/components/common";
import { Users, Plus, UploadCloud, UserCog, KeyRound, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Employees() {
  const { t } = useLang();
  const { user } = useAuth();
  const ROLES = { medarbetare: t("role_medarbetare"), teamledare: t("role_teamledare"), chef: t("role_chef"), creator: t("role_creator"), hr_admin: t("role_hr"), sysadmin: t("role_sysadmin") };
  const { data: emps, reload } = useApi("/employees");
  const { data: users, reload: reloadUsers } = useApi("/auth/users");
  const { data: departments } = useApi("/departments");
  const { data: areas } = useApi("/business_areas");
  const areaName = (id) => (areas || []).find((a) => a.id === id)?.name;
  const [form, setForm] = useState({ first_name: "", last_name: "", employee_no: "", external_ref: "", email: "" });
  const [csv, setCsv] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [acct, setAcct] = useState({ email: "", name: "", role: "medarbetare", password: "", employee_id: "", department_id: "", send_invite: true });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addEmp = async () => {
    try { await api.post("/employees", form); setForm({ first_name: "", last_name: "", employee_no: "", external_ref: "", email: "" }); setAddOpen(false); reload(); toast.success(t("emp_added")); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const importCsv = async () => {
    try { const r = await api.post("/employees/import-csv", { csv_text: csv }); toast.success(t("csv_imported", { n: r.data.created })); setCsv(""); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const createAcct = async () => {
    try {
      const r = await api.post("/auth/users", acct);
      toast.success(r.data.invited ? t("acct_invited") : t("acct_created"));
      setAcct({ email: "", name: "", role: "medarbetare", password: "", employee_id: "", department_id: "", send_invite: true });
      reloadUsers();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delEmp = async (e) => {
    if (!window.confirm(`Ta bort ${e.first_name} ${e.last_name}?`)) return;
    try { await api.delete(`/employees/${e.id}`); reload(); toast.success("Medarbetare borttagen"); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const delUser = async (u) => {
    if (!window.confirm(`Ta bort kontot ${u.name || u.email}?`)) return;
    try { await api.delete(`/auth/users/${u.id}`); reloadUsers(); toast.success("Konto borttaget"); }
    catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div>
      <PageHeader title={t("emp_title")} icon={Users} subtitle={t("emp_sub")} />
      <Tabs defaultValue="emps">
        <TabsList>
          <TabsTrigger value="emps" data-testid="tab-emps">{t("tab_emps")}</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="tab-accounts">{t("tab_accounts")}</TabsTrigger>
        </TabsList>

        <TabsContent value="emps" className="mt-4">
          <div className="mb-3 flex flex-wrap justify-end gap-2">
            <Dialog>
              <DialogTrigger asChild><Button variant="outline" data-testid="import-csv-btn"><UploadCloud className="mr-1 h-4 w-4" />{t("csv_import")}</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("import_emps_title")}</DialogTitle></DialogHeader>
                <p className="text-xs text-muted-foreground">{t("import_emps_cols")}</p>
                <Textarea rows={8} className="font-mono text-xs" value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={"first_name,last_name,employee_no,external_ref,email"} data-testid="emp-csv" />
                <DialogFooter><Button onClick={importCsv} data-testid="run-emp-csv">{t("import_btn")}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild><Button data-testid="add-emp-btn"><Plus className="mr-1 h-4 w-4" />{t("new_emp")}</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("new_emp")}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{t("first_name")}</Label><Input value={form.first_name} onChange={set("first_name")} data-testid="emp-first" /></div>
                  <div><Label>{t("last_name")}</Label><Input value={form.last_name} onChange={set("last_name")} data-testid="emp-last" /></div>
                  <div><Label>{t("emp_no")}</Label><Input value={form.employee_no} onChange={set("employee_no")} /></div>
                  <div><Label>{t("ext_ref")}</Label><Input value={form.external_ref} onChange={set("external_ref")} /></div>
                  <div className="col-span-2"><Label>{t("email")}</Label><Input value={form.email} onChange={set("email")} /></div>
                </div>
                <DialogFooter><Button onClick={addEmp} data-testid="save-emp">{t("save")}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {(emps || []).length === 0 ? <EmptyState icon={Users} title={t("no_emps")} text={t("no_emps_text")} /> : (
            <Card className="overflow-x-auto kr-scroll">
              <Table>
                <TableHeader><TableRow><TableHead>{t("th_name")}</TableHead><TableHead>{t("th_empno")}</TableHead><TableHead>{t("th_extref")}</TableHead><TableHead>{t("email")}</TableHead><TableHead>{t("th_available")}</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
                <TableBody>
                  {emps.map((e) => (
                    <TableRow key={e.id} data-testid={`emp-row-${e.id}`}>
                      <TableCell className="font-medium">{e.first_name} {e.last_name}</TableCell>
                      <TableCell className="font-mono text-xs">{e.employee_no || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{e.external_ref || "—"}</TableCell>
                      <TableCell className="text-xs">{e.email || "—"}</TableCell>
                      <TableCell>{e.available === false ? <Badge variant="destructive">{t("not_available")}</Badge> : <Badge variant="secondary">{t("yes")}</Badge>}</TableCell>
                      <TableCell><button onClick={() => delEmp(e)} data-testid={`del-emp-${e.id}`} title="Ta bort"><Trash2 className="h-4 w-4 text-red-500" /></button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="accounts" className="mt-4">
          <Card className="mb-4 p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold"><UserCog className="h-4 w-4 text-accent" />{t("create_login")}</div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <div><Label>{t("name")}</Label><Input value={acct.name} onChange={(e) => setAcct((a) => ({ ...a, name: e.target.value }))} data-testid="acct-name" /></div>
              <div><Label>{t("email")}</Label><Input value={acct.email} onChange={(e) => setAcct((a) => ({ ...a, email: e.target.value }))} data-testid="acct-email" /></div>
              <div><Label>{t("password")}</Label><Input type="text" value={acct.password} disabled={acct.send_invite} placeholder={acct.send_invite ? t("pw_chosen_by") : ""} onChange={(e) => setAcct((a) => ({ ...a, password: e.target.value }))} data-testid="acct-password" /></div>
              <div><Label>{t("role_lbl")}</Label>
                <Select value={acct.role} onValueChange={(v) => setAcct((a) => ({ ...a, role: v }))}>
                  <SelectTrigger data-testid="acct-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ROLES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>{t("link_emp")}</Label>
                <Select value={acct.employee_id || "none"} onValueChange={(v) => setAcct((a) => ({ ...a, employee_id: v === "none" ? "" : v, department_id: v === "none" ? a.department_id : "" }))}>
                  <SelectTrigger data-testid="acct-emp"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">{t("none")}</SelectItem>{(emps || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>{t("dep_lbl")}</Label>
                <Select value={acct.department_id || "none"} disabled={!!acct.employee_id} onValueChange={(v) => setAcct((a) => ({ ...a, department_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="acct-dept"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{(departments || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}{areaName(d.business_area_id) ? ` · ${areaName(d.business_area_id)}` : ""}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{t("acct_dept_hint")}</p>
            <label className="mt-3 flex items-center gap-2 text-sm" data-testid="acct-invite-toggle">
              <Checkbox checked={acct.send_invite} onCheckedChange={(v) => setAcct((a) => ({ ...a, send_invite: !!v }))} />
              <Mail className="h-4 w-4 text-accent" /> {t("send_invite_lbl")}
            </label>
            <Button className="mt-3" onClick={createAcct} data-testid="create-account"><KeyRound className="mr-1 h-4 w-4" />{acct.send_invite ? t("create_send_invite") : t("create_account_only")}</Button>
          </Card>
          <Card className="overflow-x-auto kr-scroll">
            <Table>
              <TableHeader><TableRow><TableHead>{t("th_name")}</TableHead><TableHead>{t("email")}</TableHead><TableHead>{t("role_lbl")}</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
              <TableBody>
                {(users || []).map((u) => {
                  const protected_ = u.email === user?.email || ["owner", "master_admin"].includes(u.role);
                  return (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}><TableCell className="font-medium">{u.name}</TableCell><TableCell className="text-xs">{u.email}</TableCell><TableCell><Badge variant="secondary">{u.role_label}</Badge></TableCell>
                    <TableCell>{!protected_ && <button onClick={() => delUser(u)} data-testid={`del-user-${u.id}`} title="Ta bort konto"><Trash2 className="h-4 w-4 text-red-500" /></button>}</TableCell></TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
